<script setup lang="ts">
/**
 * RegisterStep1Identity — step 1 of the resident registration flow.
 * Collects: first name, last name, email, mobile, address, lot, resident type.
 * In the real CIAM flow, "Continue" triggers MSAL signup redirect.
 */
import { ref } from 'vue';

type Step1Data = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address: string;
  lot: string;
  residentType: string;
};

const emit = defineEmits<{
  (e: 'next', data: Step1Data): void;
  (e: 'back'): void;
}>();

const firstName = ref('');
const lastName = ref('');
const email = ref('');
const phone = ref('');
const address = ref('');
const lot = ref('');
const residentType = ref('homeowner-primary');
const agreed = ref(false);

const canContinue = () =>
  firstName.value.trim().length > 0 &&
  lastName.value.trim().length > 0 &&
  email.value.trim().length > 0 &&
  address.value.trim().length > 0 &&
  agreed.value;

function onNext() {
  if (!canContinue()) return;
  emit('next', {
    firstName: firstName.value.trim(),
    lastName: lastName.value.trim(),
    email: email.value.trim(),
    phone: phone.value.trim(),
    address: address.value.trim(),
    lot: lot.value.trim(),
    residentType: residentType.value,
  });
}
</script>

<template>
  <div data-step="1" data-testid="register-step-1">
    <div class="reg-head">
      <h1 class="reg-title">Create your resident account</h1>
      <p class="reg-sub">Get text alerts on community issues, track what you've reported, and see what the board is working on.</p>
    </div>

    <div class="reg-body stack gap-16">
      <!-- HOA crest (read-only — set from URL param) -->
      <div class="hoa-picker-card">
        <div class="crest">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M3 11.5L12 4l9 7.5" /><path d="M5 10v10h14V10" /><circle cx="12" cy="14" r="2.2" fill="currentColor" stroke="none" />
          </svg>
        </div>
        <div>
          <div class="nm">Cypress Ridge HOA</div>
          <div class="meta">Austin, TX · 412 homes · founded 1998</div>
        </div>
      </div>

      <!-- Name row -->
      <div class="field-row">
        <div class="field">
          <label class="label" for="reg-first">First name</label>
          <input id="reg-first" v-model="firstName" class="input" type="text" placeholder="Jane" data-testid="reg-first-name">
        </div>
        <div class="field">
          <label class="label" for="reg-last">Last name</label>
          <input id="reg-last" v-model="lastName" class="input" type="text" placeholder="Doe" data-testid="reg-last-name">
        </div>
      </div>

      <!-- Email -->
      <div class="field">
        <label class="label" for="reg-email">Email</label>
        <input id="reg-email" v-model="email" class="input" type="email" placeholder="you@example.com" data-testid="reg-email">
        <span class="field-help">We'll send issue updates here, never to your neighbors.</span>
      </div>

      <!-- Phone + address row -->
      <div class="field-row">
        <div class="field">
          <label class="label" for="reg-phone">
            Mobile number
            <span class="label-hint">Optional — for urgent text alerts</span>
          </label>
          <input id="reg-phone" v-model="phone" class="input" type="tel" placeholder="(555) 123-4567" data-testid="reg-phone">
        </div>
        <div class="field">
          <label class="label" for="reg-address">Property address</label>
          <input id="reg-address" v-model="address" class="input" type="text" placeholder="Street address" data-testid="reg-address">
        </div>
      </div>

      <!-- Lot + resident type -->
      <div class="field-row">
        <div class="field">
          <label class="label" for="reg-lot">Lot / unit #</label>
          <input id="reg-lot" v-model="lot" class="input" type="text" placeholder="Lot 12" data-testid="reg-lot">
        </div>
        <div class="field">
          <label class="label" for="reg-type">Resident type</label>
          <select id="reg-type" v-model="residentType" class="select" data-testid="reg-resident-type">
            <option value="homeowner-primary">Homeowner (primary resident)</option>
            <option value="homeowner-renting">Homeowner (renting out)</option>
            <option value="tenant">Tenant</option>
            <option value="other">Other</option>
          </select>
        </div>
      </div>

      <!-- Terms agreement -->
      <label class="checkbox-row" style="margin-top: 4px; font-size: 13px;">
        <input v-model="agreed" type="checkbox" data-testid="reg-agree-checkbox">
        I agree to the
        <a href="#" style="color: var(--navy-700); font-weight: 500;">community guidelines</a>
        and <a href="#" style="color: var(--navy-700); font-weight: 500;">privacy policy</a>.
      </label>
    </div>

    <div class="reg-foot">
      <a href="/resident/login" class="small">&larr; Already have an account</a>
      <button
        class="btn btn-primary"
        :disabled="!canContinue()"
        data-testid="reg-step1-next"
        data-action="next"
        type="button"
        @click="onNext"
      >
        Continue &rarr;
      </button>
    </div>
  </div>
</template>
